import React, { useState, useEffect } from 'react';

interface TypingAnimationProps {
  messages: string[];
  typingSpeed?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
  emptyPauseDuration?: number;
}

const TypingAnimation: React.FC<TypingAnimationProps> = ({
  messages,
  typingSpeed = 100,
  pauseDuration = 1000,
  deletingSpeed = 50,
  emptyPauseDuration = 150
}) => {
  // Persist animation state to survive component remounts during loading
  const [currentMessageIndex, setCurrentMessageIndex] = useState(() => {
    try {
      const saved = sessionStorage.getItem('typing-animation-state');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if state is too old (>30 seconds) or if it's a page reload
        const isPageReload = performance.getEntriesByType('navigation')[0]?.type === 'reload';
        const isStateStale = parsed.timestamp && (Date.now() - parsed.timestamp) > 30000;
        
        if (isPageReload || isStateStale) {
          // Clear stale state and start fresh
          sessionStorage.removeItem('typing-animation-state');
          return 0;
        }
        
        return Math.min(parsed.messageIndex || 0, messages.length - 1);
      }
    } catch {
      // Ignore errors, use default
    }
    return 0;
  });
  
  const [currentText, setCurrentText] = useState(() => {
    try {
      const saved = sessionStorage.getItem('typing-animation-state');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if state is too old or if it's a page reload (consistent with above)
        const isPageReload = performance.getEntriesByType('navigation')[0]?.type === 'reload';
        const isStateStale = parsed.timestamp && (Date.now() - parsed.timestamp) > 30000;
        
        if (isPageReload || isStateStale) {
          return '';
        }
        
        return parsed.currentText || '';
      }
    } catch {
      // Ignore errors, use default
    }
    return '';
  });
  
  const [isTyping, setIsTyping] = useState(() => {
    try {
      const saved = sessionStorage.getItem('typing-animation-state');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if state is too old or if it's a page reload (consistent with above)
        const isPageReload = performance.getEntriesByType('navigation')[0]?.type === 'reload';
        const isStateStale = parsed.timestamp && (Date.now() - parsed.timestamp) > 30000;
        
        if (isPageReload || isStateStale) {
          return true;
        }
        
        return parsed.isTyping !== false; // Default to true if not specified
      }
    } catch {
      // Ignore errors, use default
    }
    return true;
  });

  // Save animation state to sessionStorage whenever it changes
  React.useEffect(() => {
    const state = {
      messageIndex: currentMessageIndex,
      currentText,
      isTyping,
      timestamp: Date.now()
    };
    try {
      sessionStorage.setItem('typing-animation-state', JSON.stringify(state));
    } catch {
      // Ignore storage errors
    }
  }, [currentMessageIndex, currentText, isTyping]); // All dependencies included

  // Debug logging to track component lifecycle
  React.useEffect(() => {
    // console.log('TypingAnimation component mounted/remounted - restored state:', {
    //   messageIndex: currentMessageIndex,
    //   currentText: currentText.substring(0, 10) + '...',
    //   isTyping
    // });
    return () => {
      // console.log('TypingAnimation component unmounted');
    };
  }, []);

  // Clear persisted state when component unmounts and we're truly done loading
  React.useEffect(() => {
    const clearStateOnPageNavigation = () => {
      try {
        sessionStorage.removeItem('typing-animation-state');
      } catch {
        // Ignore errors
      }
    };

    // Clear state when page navigation happens (user navigated away from loading)
    window.addEventListener('beforeunload', clearStateOnPageNavigation);
    
    return () => {
      window.removeEventListener('beforeunload', clearStateOnPageNavigation);
    };
  }, []);

  React.useEffect(() => {
    // console.log('TypingAnimation messages prop changed:', messages.length, 'messages');
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) return;

    const currentMessage = messages[currentMessageIndex];
    
    let timeout: NodeJS.Timeout;

    if (isTyping) {
      // Typing phase
      if (currentText.length < currentMessage.length) {
        timeout = setTimeout(() => {
          setCurrentText(currentMessage.slice(0, currentText.length + 1));
        }, typingSpeed);
      } else {
        // Finished typing, pause then start deleting
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, pauseDuration);
      }
    } else {
      // Deleting phase
      if (currentText.length > 0) {
        timeout = setTimeout(() => {
          setCurrentText(currentText.slice(0, -1));
        }, deletingSpeed);
      } else {
        // Finished deleting, pause briefly before starting next message
        timeout = setTimeout(() => {
          setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
          setIsTyping(true);
        }, emptyPauseDuration);
      }
    }

    return () => clearTimeout(timeout);
  }, [currentText, currentMessageIndex, isTyping, messages, typingSpeed, pauseDuration, deletingSpeed, emptyPauseDuration]);

  return (
    <div className="text-gray-700 font-medium text-center min-h-[1.5em] flex items-center justify-center">
      <span>{currentText}</span>
      <span className="animate-pulse ml-1">|</span>
    </div>
  );
};

export default TypingAnimation;